import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ROUTES, generatePath } from '@/constants/routes'
import {
  ExamQuestionReportItem,
  ExamSessionDetail,
  fetchExamSessionDetail,
  fetchExamSessionReport,
  submitExamAnswer,
  submitExamSession,
  gradeQuestion,
  ExamGradeResponse,
  fetchExamSessionProgress,
  saveExamSessionProgress,
  ExamSessionProgress,
  onExamSubmit,
  OfflineSplitResultItem
} from '../../api/exam'
import { LatexText } from '../../components/ui/LatexText'
import { getFileUrl, uploadFile } from '../../api/qb'
import { HandwritingPad } from '../../handwriting/HandwritingPad'
import { Eraser, Pencil, Trash2, Undo2, Keyboard, PenTool, ChevronLeft, ChevronRight, Brain, Star, Upload, X, Loader2, ImagePlus } from 'lucide-react'
import { groupQuestions, type QuestionItem, type GroupItem } from '../../lib/questionGroups'
import { CompositeQuestion } from './components/CompositeQuestion'
import { OfflineAnswerPanel } from './components/OfflineAnswerPanel'

const GROUP_TYPE_LABEL: Record<string, string> = {
  reading_comp: '阅读',
  cloze: '完形',
  listening: '听力',
  seven_choose_five: '七选五',
  grammar_fill: '语法',
  matching: '匹配',
}

type AnswerState = {
  value: string
  images: string[]
  strokes?: string
}

import { PuzzleContainer } from '../../components/puzzle/PuzzleContainer'
import { PuzzleTopology, PuzzleAnswer } from '@/types/puzzle'

export default function ExamSessionPage() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const [session, setSession] = useState<ExamSessionDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [gradingProgress, setGradingProgress] = useState<{ current: number; total: number } | null>(null)
  const [summary, setSummary] = useState<{
    total_score?: number | null
    correct_count?: number | null
    wrong_count?: number | null
    unanswered_count?: number | null
  } | null>(null)
  const [knowledgeSummary, setKnowledgeSummary] = useState<import('../../api/exam').KnowledgeSummary | null>(null)
  const [questions, setQuestions] = useState<ExamQuestionReportItem[]>([])
  const [answerMap, setAnswerMap] = useState<Record<string, AnswerState>>({})
  const [currentIndex, setCurrentIndex] = useState(0)
  const [sidebarVisible, setSidebarVisible] = useState(false)
  const [answerModeMap, setAnswerModeMap] = useState<Record<string, 'text' | 'handwriting' | 'upload'>>({})
  const [gradingMap, setGradingMap] = useState<Record<string, boolean>>({})
  const [handwritingDataMap, setHandwritingDataMap] = useState<Record<string, unknown>>({})
  const [progressReady, setProgressReady] = useState(false)
  const persistedRef = useRef<{
    answerMap?: Record<string, AnswerState>
    answerModeMap?: Record<string, 'text' | 'handwriting' | 'upload'>
    currentIndex?: number
  } | null>(null)
  const persistedKey = sessionId ? `exam_session_state:${sessionId}` : null
  const saveTimerRef = useRef<number | null>(null)
  const uploadInputRef = useRef<HTMLInputElement>(null)
  const [uploadingMap, setUploadingMap] = useState<Record<string, boolean>>({})
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false)

  useEffect(() => {
    if (!persistedKey) return
    const raw = localStorage.getItem(persistedKey)
    if (!raw) {
      persistedRef.current = null
      return
    }
    try {
      persistedRef.current = JSON.parse(raw)
    } catch {
      persistedRef.current = null
    }
  }, [persistedKey])

  useEffect(() => {
    if (!sessionId) return
    let mounted = true
    setLoading(true)
    Promise.all([
      fetchExamSessionDetail(sessionId),
      fetchExamSessionReport(sessionId),
      fetchExamSessionProgress(sessionId).catch(() => ({} as ExamSessionProgress)),
    ])
      .then(([detail, report, progress]) => {
        if (!mounted) return
        setSession(detail)
        setQuestions(report.questions || [])
        const initialAnswers: Record<string, AnswerState> = {}
        report.questions.forEach((q) => {
          if (!q.question_id) return
          const val = (q.user_answer as any)?.value
          const images = ((q.user_answer as any)?.images as string[] | undefined) || []
          const strokes = (q.user_answer as any)?.strokes as string | undefined
          if ((typeof val === 'string' && val) || images.length > 0 || strokes) {
            initialAnswers[q.question_id] = { value: val || '', images, strokes }
          }
        })
        const persisted = persistedRef.current
        const hasProgress =
          !!progress.answerMap ||
          !!progress.answerModeMap ||
          typeof progress.currentIndex === 'number'
        const progressAnswerMap = (hasProgress ? progress.answerMap : null) as
          | Record<string, AnswerState>
          | null
        const fallbackAnswerMap = (!hasProgress ? persisted?.answerMap : null) as
          | Record<string, AnswerState>
          | null
        const merged = { ...initialAnswers }
        const mergeFrom = progressAnswerMap || fallbackAnswerMap
        if (mergeFrom) {
          Object.entries(mergeFrom).forEach(([key, value]) => {
            if (!value) return
            const hasContent = !!value.value || (value.images || []).length > 0 || !!value.strokes
            if (hasContent) {
              merged[key] = value
            }
          })
        }
        if (mergeFrom) {
          setAnswerMap(merged)
        } else {
          setAnswerMap(initialAnswers)
        }
        const nextModeMap = (hasProgress ? progress.answerModeMap : null) || persisted?.answerModeMap
        if (nextModeMap) {
          setAnswerModeMap(nextModeMap as Record<string, 'text' | 'handwriting' | 'upload'>)
        }
        const nextIndex =
          typeof progress.currentIndex === 'number'
            ? progress.currentIndex
            : typeof persisted?.currentIndex === 'number'
              ? persisted.currentIndex
              : null
        if (typeof nextIndex === 'number') {
          const bounded = Math.max(0, Math.min(nextIndex, report.questions.length - 1))
          setCurrentIndex(bounded)
        }
        if (
          typeof report.total_score === 'number' ||
          typeof report.correct_count === 'number' ||
          typeof report.wrong_count === 'number' ||
          typeof report.unanswered_count === 'number'
        ) {
          setSummary({
            total_score: report.total_score,
            correct_count: report.correct_count,
            wrong_count: report.wrong_count,
            unanswered_count: report.unanswered_count,
          })
        } else {
          setSummary(null)
        }
        setProgressReady(true)
      })
      .catch((err: unknown) => {
        if (mounted) {
          setError(err instanceof Error ? err.message : String(err))
        }
      })
      .finally(() => {
        if (mounted) {
          setLoading(false)
        }
      })
    return () => {
      mounted = false
    }
  }, [sessionId])

  useEffect(() => {
    if (!persistedKey) return
    const payload = {
      answerMap,
      answerModeMap,
      currentIndex,
    }
    localStorage.setItem(persistedKey, JSON.stringify(payload))
  }, [persistedKey, answerMap, answerModeMap, currentIndex])

  useEffect(() => {
    if (!sessionId || !progressReady) return
    if (session?.status !== 'in_progress') return
    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current)
    }
    saveTimerRef.current = window.setTimeout(() => {
      saveExamSessionProgress(sessionId, {
        answerMap: answerMap,
        answerModeMap: answerModeMap,
        currentIndex: currentIndex,
      }).catch(() => {})
    }, 800)
    return () => {
      if (saveTimerRef.current) {
        window.clearTimeout(saveTimerRef.current)
      }
    }
  }, [sessionId, progressReady, session?.status, answerMap, answerModeMap, currentIndex])

  const saveCurrentHandwriting = async () => {
    // New HandwritingPad auto-saves via onChange callback
    // No manual save needed
  }

  const handleSwitchQuestion = async (idx: number) => {
    await saveCurrentHandwriting()
    setCurrentIndex(idx)
  }

  /** Batch AI-grade all answered but ungraded questions before submission */
  async function batchGradeUngraded() {
    if (!sessionId) return

    // Find questions that have an answer but no AI grading result
    const toGrade = questions.filter((q) => {
      if (!q.question_id) return false
      const hasAnswer = !!answerMap[q.question_id]?.value || (answerMap[q.question_id]?.images?.length ?? 0) > 0
      const alreadyGraded = q.is_correct !== null && q.is_correct !== undefined
      return hasAnswer && !alreadyGraded
    })

    if (toGrade.length === 0) return

    setGradingProgress({ current: 0, total: toGrade.length })

    const CONCURRENCY = 3
    let completed = 0

    // Process in batches of CONCURRENCY
    for (let i = 0; i < toGrade.length; i += CONCURRENCY) {
      const batch = toGrade.slice(i, i + CONCURRENCY)
      await Promise.allSettled(
        batch.map(async (q) => {
          try {
            const answerImages = answerMap[q.question_id!]?.images || []
            const imageUrl = answerImages.length > 0 ? getFileUrl(answerImages[0]) : undefined

            const result = await gradeQuestion({
              sessionId,
              questionId: q.question_id!,
              imageUrl,
              userAnswer: { value: answerMap[q.question_id!]?.value || '' },
              purpose: 'submit',
            })

            // Update question state with grading result
            setQuestions((prev) =>
              prev.map((item) =>
                item.question_id === q.question_id
                  ? {
                      ...item,
                      score_obtained: result.score,
                      is_correct: result.isCorrect,
                      feedback: result.feedback,
                      analysis: result.analysis || undefined,
                      correct_answer: result.correctAnswer || undefined,
                      is_marked: true,
                    }
                  : item,
              ),
            )
          } catch (err) {
            // Single question failure doesn't block submission
            console.error(`Failed to grade question ${q.question_id}:`, err)
          }
        }),
      )
      completed += batch.length
      setGradingProgress({ current: completed, total: toGrade.length })
    }

    setGradingProgress(null)
  }

  async function handleSubmit() {
    if (!sessionId) return
    try {
      await saveCurrentHandwriting()
      setSubmitting(true)
      // AI-grade all answered but ungraded questions first
      await batchGradeUngraded()
      const submitResult = await submitExamSession(sessionId)
      const r = await fetchExamSessionReport(sessionId)
      setQuestions(r.questions || [])
      setSession((prev) => (prev ? { ...prev, status: 'submitted' } : prev))
      setSummary({
        total_score: r.total_score,
        correct_count: r.correct_count,
        wrong_count: r.wrong_count,
        unanswered_count: r.unanswered_count,
      })
      if (submitResult.knowledgeSummary) {
        setKnowledgeSummary(submitResult.knowledgeSummary)
      }
      // Fire-and-forget: trigger AI diagnosis in background
      onExamSubmit(sessionId).catch(() => {})
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSubmitting(false)
    }
  }

  async function handleSelectChoice(q: ExamQuestionReportItem, value: string) {
    if (!sessionId || !q.question_id) return

    // Toggle: if already selected, deselect
    const currentAnswer = answerMap[q.question_id]
    const newValue = currentAnswer?.value === value ? '' : value

    setAnswerMap((prev) => ({
      ...prev,
      [q.question_id]: { value: newValue, images: [] },
    }))

    if (session?.status === 'in_progress') {
      try {
        await submitExamAnswer(sessionId, {
          questionId: q.question_id,
          userAnswer: { value: newValue, images: [] },
        })
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err))
      }
    }
  }

  async function handleTextAnswer(questionId: string, value: string) {
    if (!sessionId || session?.status !== 'in_progress') return
    setAnswerMap((prev) => ({
      ...prev,
      [questionId]: { value, images: prev[questionId]?.images || [] },
    }))
    try {
      await submitExamAnswer(sessionId, {
        questionId,
        userAnswer: { value, images: [] },
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  async function handleUploadAnswer(questionId: string, file: File) {
    if (!sessionId || session?.status !== 'in_progress') return
    setUploadingMap((prev) => ({ ...prev, [questionId]: true }))
    try {
      const uploaded = await uploadFile(file)
      const url = getFileUrl(uploaded.url)
      setAnswerMap((prev) => ({
        ...prev,
        [questionId]: { value: '', images: [url] },
      }))
      await submitExamAnswer(sessionId, {
        questionId,
        userAnswer: { value: '', images: [url] },
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setUploadingMap((prev) => ({ ...prev, [questionId]: false }))
    }
  }

  async function handlePuzzleAnswer(q: ExamQuestionReportItem, values: PuzzleAnswer) {
      if (!sessionId || session?.status !== 'in_progress') return
      if (!q.question_id) return
      let nextAnswer: AnswerState | null = null
      setAnswerMap((prev) => {
        const prevAnswer = prev[q.question_id] || { value: '', images: [], strokes: undefined }
        nextAnswer = {
          value: values as any,
          images: prevAnswer.images || [],
          strokes: prevAnswer.strokes,
        }
        return {
          ...prev,
          [q.question_id]: nextAnswer,
        }
      })
      try {
        if (!nextAnswer) return
        await submitExamAnswer(sessionId, {
          questionId: q.question_id,
          userAnswer: nextAnswer,
        })
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err))
      }
    }

  // Group questions by group_id for composite rendering
  const groupItems = useMemo(() => groupQuestions(questions), [questions])

  // Group items by section for sidebar display
  const sections = useMemo(() => {
    const getSection = (item: QuestionItem): string => {
      if (item.kind === 'single') return item.question.section || '其他'
      return item.subQuestions[0]?.section || '其他'
    }
    const result: { section: string; items: QuestionItem[] }[] = []
    const sectionMap = new Map<string, QuestionItem[]>()
    groupItems.forEach(item => {
      const sec = getSection(item)
      if (!sectionMap.has(sec)) {
        sectionMap.set(sec, [])
        result.push({ section: sec, items: sectionMap.get(sec)! })
      }
      sectionMap.get(sec)!.push(item)
    })
    return result
  }, [groupItems])

  const currentQuestion = useMemo(
    () => (questions.length > 0 ? questions[Math.min(currentIndex, questions.length - 1)] : null),
    [questions, currentIndex],
  )

  // 测试/线下模式锁定：作答中（提交前）隐藏并禁用所有智能讲解/批改入口，提交后统一批改
  const testLocked = (session?.mode === 'test' || session?.mode === 'offline') && session.status === 'in_progress'

  /** 线下作答：识别结果确认填入后同步到 answerMap */
  function handleOfflineResults(results: OfflineSplitResultItem[]) {
    setAnswerMap(prev => {
      const next = { ...prev }
      for (const r of results) {
        if (r.matched) {
          next[r.questionId] = { value: r.extractedAnswer, images: [] }
        }
      }
      return next
    })
  }

  const handleExplain = (qId: string) => {
    // 测试模式锁定期间禁止讲解（按钮已隐藏，此处做双保险）
    if (testLocked) return
    const q = questions.find(item => item.question_id === qId)
    if (!q) return
    
    // Construct a comprehensive question context
    const content = q.content || {}
    const stem = content.stem || ''
    const options = q.options || []
    
    // Construct a rich text representation of the question
    let questionText = stem
    
    // Append options if available
    if (options.length > 0) {
      questionText += '\n\n选项：\n'
      options.forEach((opt, idx) => {
        const label = opt.label || String.fromCharCode(65 + idx)
        questionText += `${label}. ${opt.content || ''}\n`
      })
    }
    
    // Append user answer if available
    const currentAnswer = answerMap[q.question_id]
    if (currentAnswer?.value) {
      questionText += `\n\n学生作答：${currentAnswer.value}`
    }
    
    // Append correct answer/analysis if available (and marked)
    if (q.analysis) {
       // We might want to include this or let the tutor figure it out.
       // Including it helps the tutor give better context if it's already known.
       questionText += `\n\n参考解析：${q.analysis}`
    }

    localStorage.setItem('tutor_context_content', questionText)
    
    // Filter out invalid or duplicate image links
    const stemText = (content.stem as string) || ''
    const isValidImageUrl = (url: string): boolean => {
      if (!url) return false
      if (/^question-[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}(-\d+)?$/i.test(url)) {
        return false
      }
      // Skip images already embedded in stem via Markdown ![](url)
      if (stemText.includes(url)) return false
      return true
    }
    const contentImages = ((content.images as string[]) || []).filter(isValidImageUrl)
    if (contentImages.length > 0) {
       const imageUrls = contentImages.map(img => getFileUrl(img))
       const imageMarkdown = imageUrls.map(url => `![题目图片](${url})`).join('\n')
       questionText = `${imageMarkdown}\n\n${questionText}`
       localStorage.setItem('tutor_context_content', questionText)
    }
    if (q.question_id) {
      localStorage.setItem('tutor_context_question_id', q.question_id)
    }
    localStorage.setItem('tutor_context_pending', 'true')

    window.open('#/tutor?mode=explain&auto_trigger=true', '_blank')
  }

  async function handleGrade(qId: string, imgUrl?: string) {
    // 测试模式锁定期间禁止逐题批改（提交时由 batchGradeUngraded 统一批改）
    if (!sessionId || testLocked) return

    // Get image URL from answerMap (set by HandwritingPad onChange)
    const answerImages = answerMap[qId]?.images || []
    let gradingImageUrl = imgUrl ? getFileUrl(imgUrl) : (answerImages.length > 0 ? getFileUrl(answerImages[0]) : undefined)
    const hasImage = !!gradingImageUrl
    const hasText = !!answerMap[qId]?.value

    // Allow grading even if empty, as user requested "always available"
    // if (!hasImage && !hasText) return

    setGradingMap((prev) => ({ ...prev, [qId]: true }))
    try {
      const result = await gradeQuestion({
        sessionId: sessionId,
        questionId: qId,
        imageUrl: gradingImageUrl,
        userAnswer: { value: answerMap[qId]?.value || '' },
      })

      // Update question state with grading result
      setQuestions(prev => prev.map(item => {
        if (item.question_id === qId) {
          return {
            ...item,
            score_obtained: result.score,
            is_correct: result.isCorrect,
            feedback: result.feedback,
            analysis: result.analysis || undefined,
            correct_answer: result.correctAnswer || undefined,
            is_marked: true
          }
        }
        return item
      }))
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setGradingMap((prev) => ({ ...prev, [qId]: false }))
    }
  }

  function renderQuestionCard(q: ExamQuestionReportItem) {
    const content = q.content || {}
    let stem: string = (content.stem as string) || ''
    // Clean up trailing question-UUID links that appear as plain text in stem
    // These are leftover from original data where images were already embedded via ![](xxx.jpg)
    stem = stem.replace(/\s*question-[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}(-\d+)?\s*$/gi, '')
    
    // Check if it's a puzzle question
    const topology = content.topology as PuzzleTopology | undefined
    
    const options = q.options || []
    // Filter out invalid or duplicate image links
    // - question-UUID format links are invalid (no extension, broken)
    // - images already referenced in stem via ![](xxx) are skipped to avoid duplicate rendering
    const isValidImageUrl = (url: string): boolean => {
      if (!url) return false
      if (/^question-[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}(-\d+)?$/i.test(url)) {
        return false
      }
      // Skip images already embedded in stem via Markdown ![](url)
      if (stem.includes(url)) return false
      return true
    }
    const contentImages = ((content.images as string[]) || []).filter(isValidImageUrl)
    const type = q.type || 'choice'
    
    const currentAnswer = answerMap[q.question_id] || {
      value: ((q.user_answer as any)?.value as string | undefined) || '',
      images: (((q.user_answer as any)?.images as string[] | undefined) || []).slice(),
      strokes: (q.user_answer as any)?.strokes as string | undefined,
    }
    const answeredVal = currentAnswer.value
    const answerImages = currentAnswer.images || []
    const hasHandwritingImage = answerImages.length > 0
    const hasStrokes = !!currentAnswer.strokes
    const mode = answerModeMap[q.question_id] || 'text'
    const isReadOnly = session?.status === 'submitted'
    const isGrading = gradingMap[q.question_id] || false

    // Puzzle Question Rendering
    if (topology) {
      const puzzleValues = (typeof answeredVal === 'object' ? answeredVal : {}) as PuzzleAnswer
      
      return (
        <section className="space-y-4">
          <div className="space-y-2">
            <div className="text-slate-900">
              <LatexText className="text-xl font-medium">{stem}</LatexText>
            </div>
          </div>
          
          <div className="py-4">
            <PuzzleContainer
              topology={topology}
              initialValues={puzzleValues}
              onChange={(vals) => handlePuzzleAnswer(q, vals)}
              readOnly={isReadOnly}
            />
          </div>

          <div className="flex flex-col h-[60vh]">
            <div className="flex items-center justify-between text-xs text-slate-500 mb-2 shrink-0">
               <span>手写笔记 / 草稿</span>
            </div>
            <HandwritingPad
              key={q.question_id}
              onChange={async (blob) => {
                const uploaded = await uploadFile(new File([blob], 'handwriting.png', { type: 'image/png' }))
                const url = getFileUrl(uploaded.url)
                setAnswerMap((prev) => ({
                  ...prev,
                  [q.question_id]: { value: '', images: [url], strokes: '' },
                }))
                if (session?.status === 'in_progress' && sessionId) {
                  await submitExamAnswer(sessionId, {
                    questionId: q.question_id,
                    userAnswer: { value: '', images: [url] },
                  })
                }
              }}
              readOnly={isReadOnly}
            />
            
            <div className="flex justify-end mt-2 gap-2">
              {currentIndex < questions.length - 1 && (
                <button
                  type="button"
                  onClick={() => handleSwitchQuestion(currentIndex + 1)}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  下一题
                  <ChevronRight className="ml-1 w-3 h-3" />
                </button>
              )}
              {!testLocked && (
              <>
              <button
                type="button"
                onClick={() => handleExplain(q.question_id)}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                <Brain className="mr-1 w-3 h-3" />
                智能讲解
              </button>
              <button
                type="button"
                onClick={() => handleGrade(q.question_id, answerImages[0])}
                disabled={isGrading || isReadOnly}
                    className={`inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white ${isGrading || isReadOnly ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50`}
              >
                {isGrading ? '正在批改...' : 'AI 智能批改'}
              </button>
              </>
              )}
            </div>
          </div>
          
          {(q.feedback || (q.analysis && q.is_marked)) && (
            <div className="mt-4 p-4 border rounded-md bg-green-50 border-green-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-green-800">批改结果</h3>
                <div className="flex items-center space-x-2">
                  <span
                    className={`px-2 py-0.5 rounded text-xs font-bold ${q.is_correct ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'}`}
                  >
                    {q.is_correct ? '正确' : '错误'}
                  </span>
                  {q.score_obtained !== undefined && (
                    <span className="text-sm font-medium text-slate-700">
                      得分: {q.score_obtained} / {q.score || 10}
                    </span>
                  )}
                </div>
              </div>

              {q.correct_answer && (
                <div className="mb-3">
                  <div className="text-xs font-semibold text-slate-600 mb-1">正确答案：</div>
                  <div className="text-sm text-slate-800 bg-white p-2 rounded border border-green-100">
                    <LatexText>{q.correct_answer}</LatexText>
                  </div>
                </div>
              )}

              {q.feedback && (
                <div className="mb-3">
                  <div className="text-xs font-semibold text-slate-600 mb-1">评语：</div>
                  <div className="text-sm text-slate-800 bg-white p-2 rounded border border-green-100">
                    <LatexText>{q.feedback}</LatexText>
                  </div>
                </div>
              )}
              
              {q.analysis && (
                <div>
                  <div className="text-xs font-semibold text-slate-600 mb-1">解析：</div>
                  <div className="text-sm text-slate-800 bg-white p-2 rounded border border-green-100">
                    <LatexText>{q.analysis}</LatexText>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Debug info if needed */}
          {/* <pre className="text-xs text-gray-400 mt-4">{JSON.stringify(puzzleValues, null, 2)}</pre> */}
        </section>
      )
    }

    if (type === 'choice' || type === '单选题') {
      return (
        <section className="space-y-4">
          <div className="space-y-2">
            <div className="text-slate-900">
            <LatexText className="text-xl font-medium">{stem}</LatexText>
          </div>
            {contentImages.length > 0 && (
              <div className="flex flex-wrap gap-3">
                {contentImages.map((img, idx) => (
                  <img
                    key={`${q.question_id}-stem-${idx}`}
                    src={getFileUrl(img)}
                    alt={`question-${q.question_id}-${idx}`}
                    className="max-h-64 rounded-md border border-slate-200 object-contain"
                  />
                ))}
              </div>
            )}
          </div>
          <div className="space-y-2">
            {options.map((opt, idx) => {
              const label = opt.label || String.fromCharCode(65 + idx)
              const text = (opt.content as string) || ''
              const isSelected = answeredVal === label
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => handleSelectChoice(q, label)}
                  disabled={isReadOnly}
                  className={[
                    'flex w-full items-center justify-between rounded-2xl border px-3 py-2 text-left text-sm transition-colors',
                    'border-slate-200 bg-white hover:bg-slate-50',
                    isSelected ? 'border-emerald-600 bg-emerald-100 text-emerald-950 shadow-sm ring-2 ring-emerald-200' : '',
                    isReadOnly ? 'cursor-not-allowed opacity-70' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <div className="flex items-start gap-2 w-full">
                    <span
                      className={[
                        'mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                        isSelected ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600',
                      ].join(' ')}
                    >
                      {label}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-800 break-words">
                        <LatexText>{text}</LatexText>
                      </div>
                      {opt.images && opt.images.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {opt.images.map((img, imgIdx) => (
                            <img
                              key={`${q.question_id}-opt-${idx}-${imgIdx}`}
                              src={getFileUrl(img)}
                              alt={`option-${label}-${imgIdx}`}
                              className="max-h-32 rounded border border-slate-200 object-contain"
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
            <div className="flex justify-end mt-2 gap-2">
              {currentIndex < questions.length - 1 && (
                <button
                  type="button"
                  onClick={() => handleSwitchQuestion(currentIndex + 1)}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  下一题
                  <ChevronRight className="ml-1 w-3 h-3" />
                </button>
              )}
              {!testLocked && (
              <>
              <button
                type="button"
                onClick={() => handleExplain(q.question_id)}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                <Brain className="mr-1 w-3 h-3" />
                智能讲解
              </button>
              <button
                type="button"
                onClick={() => handleGrade(q.question_id)}
              disabled={isGrading || isReadOnly}
              className={`inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white ${
                isGrading || isReadOnly ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50`}
            >
              {isGrading ? '正在批改...' : 'AI 智能批改'}
            </button>
            </>
            )}
          </div>
        </section>
      )
    }


    return (
      <section className="space-y-4">
        <div className="space-y-2">
          <div className="text-slate-900">
            <LatexText className="text-xl font-medium">{stem}</LatexText>
          </div>
          {contentImages.length > 0 && (
            <div className="flex flex-wrap gap-3">
              {contentImages.map((img, idx) => (
                <img
                  key={`${q.question_id}-stem-${idx}`}
                  src={getFileUrl(img)}
                  alt={`question-${q.question_id}-${idx}`}
                  className="max-h-64 rounded-md border border-slate-200 object-contain"
                />
              ))}
            </div>
          )}
        </div>
        <div className="space-y-3">
          <div className="inline-flex rounded-full border bg-slate-50 p-0.5 text-xs">
            <button
              type="button"
              onClick={() =>
                setAnswerModeMap((prev) => ({
                  ...prev,
                  [q.question_id]: 'text',
                }))
              }
              className={[
                'px-3 py-1 rounded-full flex items-center gap-1.5',
                mode === 'text' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <Keyboard className="w-3.5 h-3.5" />
              键盘作答
            </button>
            <button
              type="button"
              onClick={() =>
                setAnswerModeMap((prev) => ({
                  ...prev,
                  [q.question_id]: 'handwriting',
                }))
              }
              className={[
                'px-3 py-1 rounded-full flex items-center gap-1.5',
                mode === 'handwriting' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <PenTool className="w-3.5 h-3.5" />
              手写作答
            </button>
            <button
              type="button"
              onClick={() =>
                setAnswerModeMap((prev) => ({
                  ...prev,
                  [q.question_id]: 'upload',
                }))
              }
              className={[
                'px-3 py-1 rounded-full flex items-center gap-1.5',
                mode === 'upload' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <ImagePlus className="w-3.5 h-3.5" />
              上传答案
            </button>
          </div>
          <div className="space-y-4">
            {mode === 'text' && (
              <>
                <textarea
                  className={[
                    'w-full min-h-[120px] rounded-md border-2 border-blue-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500',
                    isReadOnly ? 'cursor-not-allowed bg-slate-50' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  placeholder="在这里作答..."
                  value={answeredVal}
                  onChange={(e) => handleTextAnswer(q.question_id, e.target.value)}
                  disabled={isReadOnly}
                />
                
                <div className="flex justify-end mt-2 gap-2">
                  {currentIndex > 0 && (
                    <button
                      type="button"
                      onClick={() => handleSwitchQuestion(currentIndex - 1)}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-slate-500 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-400"
                    >
                      <ChevronLeft className="mr-1 w-3 h-3" />
                      上一题
                    </button>
                  )}
                  {currentIndex < questions.length - 1 && (
                    <button
                      type="button"
                      onClick={() => handleSwitchQuestion(currentIndex + 1)}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      下一题
                      <ChevronRight className="ml-1 w-3 h-3" />
                    </button>
                  )}
                  {!testLocked && (
                  <>
                  <button
                    type="button"
                    onClick={() => handleExplain(q.question_id)}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                  >
                    <Brain className="mr-1 w-3 h-3" />
                    智能讲解
                  </button>
                  <button
                    type="button"
                    onClick={() => handleGrade(q.question_id)}
                    disabled={isGrading || isReadOnly}
                    className={`inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white ${
                      isGrading || isReadOnly ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700'
                    } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50`}
                  >
                    {isGrading ? '正在批改...' : 'AI 智能批改'}
                  </button>
                  </>
                  )}
                </div>
              </>
            )}
            {mode === 'handwriting' && (
              <HandwritingPad
                key={q.question_id}
                fullscreen
                onChange={async (blob) => {
                  const uploaded = await uploadFile(new File([blob], 'handwriting.png', { type: 'image/png' }))
                  const url = getFileUrl(uploaded.url)
                  setAnswerMap((prev) => ({
                    ...prev,
                    [q.question_id]: { value: '', images: [url], strokes: '' },
                  }))
                  if (session?.status === 'in_progress' && sessionId) {
                    await submitExamAnswer(sessionId, {
                      questionId: q.question_id,
                      userAnswer: { value: '', images: [url] },
                    })
                  }
                }}
                initialData={handwritingDataMap[q.question_id]}
                onClose={(data) => {
                  if (data) {
                    setHandwritingDataMap((prev) => ({ ...prev, [q.question_id]: data }))
                  }
                  setAnswerModeMap((prev) => ({
                    ...prev,
                    [q.question_id]: 'text',
                  }))
                }}
                readOnly={isReadOnly}
                questionIndex={currentIndex}
                questionScore={q.score ?? undefined}
                questionStem={stem}
                questionImages={contentImages.map((img) => getFileUrl(img))}
                onPrevQuestion={currentIndex > 0 ? () => handleSwitchQuestion(currentIndex - 1) : undefined}
                onNextQuestion={currentIndex < questions.length - 1 ? () => handleSwitchQuestion(currentIndex + 1) : undefined}
                onExplain={testLocked ? undefined : () => handleExplain(q.question_id)}
                onGrade={testLocked ? undefined : () => handleGrade(q.question_id, answerImages[0])}
                canGoPrev={currentIndex > 0}
                canGoNext={currentIndex < questions.length - 1}
                isGrading={isGrading}
              />
            )}
            {mode === 'upload' && (
              <div className="space-y-3">
                {uploadingMap[q.question_id] ? (
                  /* Uploading state */
                  <div className="flex flex-col items-center justify-center border-2 border-dashed border-blue-300 rounded-lg p-8 bg-blue-50">
                    <Loader2 className="h-8 w-8 text-blue-500 animate-spin mb-2" />
                    <p className="text-sm text-blue-600">正在上传图片...</p>
                  </div>
                ) : answerImages.length === 0 ? (
                  /* Upload area */
                  <div
                    className="flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-lg p-8 bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer"
                    onClick={() => uploadInputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation() }}
                    onDrop={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      const file = e.dataTransfer.files?.[0]
                      if (file && file.type.startsWith('image/')) {
                        handleUploadAnswer(q.question_id!, file)
                      }
                    }}
                  >
                    <input
                      ref={uploadInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (file) handleUploadAnswer(q.question_id!, file)
                        e.target.value = ''
                      }}
                    />
                    <Upload className="h-8 w-8 text-slate-400 mb-2" />
                    <p className="text-sm text-slate-500">点击或拖拽上传答案图片</p>
                    <p className="text-xs text-slate-400 mt-1">支持 JPG、PNG、WebP</p>
                  </div>
                ) : (
                  /* Image preview */
                  <div className="space-y-2">
                    <div className="relative inline-block">
                      <img
                        src={answerImages[0]}
                        alt="答案图片"
                        className="max-h-64 rounded-md border border-slate-200 object-contain"
                      />
                      {!isReadOnly && (
                        <button
                          type="button"
                          onClick={() => {
                            setAnswerMap((prev) => ({
                              ...prev,
                              [q.question_id]: { value: '', images: [] },
                            }))
                          }}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    {!isReadOnly && (
                      <button
                        type="button"
                        onClick={() => uploadInputRef.current?.click()}
                        className="text-xs text-blue-600 hover:text-blue-800 underline"
                      >
                        重新上传
                      </button>
                    )}
                    <input
                      ref={uploadInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (file) handleUploadAnswer(q.question_id!, file)
                        e.target.value = ''
                      }}
                    />
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex justify-end mt-2 gap-2">
                  {currentIndex > 0 && (
                    <button
                      type="button"
                      onClick={() => handleSwitchQuestion(currentIndex - 1)}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-slate-500 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-400"
                    >
                      <ChevronLeft className="mr-1 w-3 h-3" />
                      上一题
                    </button>
                  )}
                  {currentIndex < questions.length - 1 && (
                    <button
                      type="button"
                      onClick={() => handleSwitchQuestion(currentIndex + 1)}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      下一题
                      <ChevronRight className="ml-1 w-3 h-3" />
                    </button>
                  )}
                  {!testLocked && (
                  <>
                  <button
                    type="button"
                    onClick={() => handleExplain(q.question_id)}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                  >
                    <Brain className="mr-1 w-3 h-3" />
                    智能讲解
                  </button>
                  <button
                    type="button"
                    onClick={() => handleGrade(q.question_id, answerImages[0])}
                    disabled={isGrading || isReadOnly}
                    className={`inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white ${
                      isGrading || isReadOnly ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700'
                    } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50`}
                  >
                    {isGrading ? '正在批改...' : 'AI 智能批改'}
                  </button>
                  </>
                  )}
                </div>
              </div>
            )}
            {(q.feedback || (q.analysis && q.is_marked)) && (
            <div className="mt-4 p-4 border rounded-md bg-green-50 border-green-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-green-800">批改结果</h3>
                  <div className="flex items-center space-x-2">
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-bold ${q.is_correct ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'}`}
                    >
                      {q.is_correct ? '正确' : '错误'}
                    </span>
                    {q.score_obtained !== undefined && (
                      <span className="text-sm font-medium text-slate-700">
                        得分: {q.score_obtained} / {q.score || 10}
                      </span>
                    )}
                  </div>
                </div>

                {q.correct_answer && (
                  <div className="mb-3">
                    <div className="text-xs font-semibold text-slate-600 mb-1">正确答案：</div>
                    <div className="text-sm text-slate-800 bg-white p-2 rounded border border-green-100">
                      <LatexText>{q.correct_answer}</LatexText>
                    </div>
                  </div>
                )}

                {q.feedback && (
                  <div className="mb-3">
                    <div className="text-xs font-semibold text-slate-600 mb-1">评语：</div>
                    <div className="text-sm text-slate-800 bg-white p-2 rounded border border-green-100">
                      <LatexText>{q.feedback}</LatexText>
                    </div>
                  </div>
                )}

                {q.analysis && (
                  <div>
                    <div className="text-xs font-semibold text-slate-600 mb-1">详细解析：</div>
                    <div className="text-sm text-slate-800 prose prose-sm max-w-none bg-white p-2 rounded border border-green-100">
                      <LatexText>{q.analysis}</LatexText>
                    </div>
                  </div>
                )}

                <div className="flex justify-end mt-2">
                  <button
                    type="button"
                    onClick={() => handleGrade(q.question_id, answerImages[0])}
                    disabled={isGrading}
                    className="text-xs text-indigo-600 hover:text-indigo-800 underline"
                  >
                    {isGrading ? '正在重新批改...' : '重新批改'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    )
  }

  return (
    <div className="p-6 pb-24 md:pb-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">
          {session ? session.paper.name : '真题练习'}
          {session?.mode === 'test' && (
            <span className="ml-3 inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 align-middle text-xs font-medium text-amber-700">
              测试模式
            </span>
          )}
          {session?.mode === 'offline' && (
            <span className="ml-3 inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-0.5 align-middle text-xs font-medium text-indigo-700">
              线下作答
            </span>
          )}
        </h1>
        <div className="flex items-center gap-3 text-sm">
          <Link to={generatePath(ROUTES.EXAM_PAPER, { paperId: session?.paper.id ?? '' })} className="text-blue-600 hover:underline">
            查看试卷信息
          </Link>
          <button
            type="button"
            onClick={async () => {
              await saveCurrentHandwriting()
              navigate(ROUTES.EXAM)
            }}
            className="text-blue-600 hover:underline"
          >
            返回试卷列表
          </button>
          {session && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || session.status === 'submitted'}
              className="inline-flex items-center px-4 py-2 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting
                ? gradingProgress
                  ? `正在批改 ${gradingProgress.current}/${gradingProgress.total} 题...`
                  : '正在提交...'
                : session.status === 'submitted'
                  ? '已提交'
                  : '提交试卷'}
            </button>
          )}
        </div>
      </div>
      {loading && <div>加载中...</div>}
      {error && <div className="text-red-500 text-sm">{error}</div>}
      {!loading && !error && session && (
        <div className="space-y-4">
          <div className="text-sm text-muted-foreground space-x-2">
            {session.paper.subject && <span>{session.paper.subject}</span>}
            {session.paper.exam_type && <span>{session.paper.exam_type}</span>}
            {session.paper.year && <span>{session.paper.year}</span>}
            {session.paper.stage && <span>{session.paper.stage}</span>}
            {session.paper.grade && <span>{session.paper.grade}</span>}
          </div>
          {testLocked && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
              {session?.mode === 'offline'
                ? '线下作答：请在下方上传作答纸照片完成识别，提交试卷后统一批改并生成完整评测报告。'
                : '测试模式：作答过程中不提供智能讲解与逐题批改；完成作答后点击「提交试卷」，系统将统一批改并生成完整评测报告。'}
            </div>
          )}
          {/* 线下作答面板：上传整卷照片、AI 识别切分 */}
          {session?.mode === 'offline' && session.status === 'in_progress' && sessionId && (
            <OfflineAnswerPanel
              sessionId={sessionId}
              paperId={session.paper.id}
              onAnswersApplied={handleOfflineResults}
            />
          )}
          <div className="flex gap-4">
            {/* Sidebar toggle */}
            <button
              type="button"
              onClick={() => setSidebarVisible(v => !v)}
              className="hidden md:flex shrink-0 self-start items-center justify-center w-7 h-7 rounded-lg border border-slate-200 bg-white text-slate-400 hover:text-indigo-500 hover:border-indigo-300 transition-colors mt-1"
              title={sidebarVisible ? '隐藏题目列表' : '显示题目列表'}
            >
              {sidebarVisible ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            {sidebarVisible && (
            <div className="hidden md:block w-56 shrink-0 border rounded-xl p-3 space-y-2 overflow-y-auto max-h-[80vh] bg-slate-50">
              <div className="text-xs font-medium text-slate-500">题目列表</div>
              <div className="space-y-3">
                {sections.map(({ section, items: sectionItems }) => (
                  <div key={section}>
                    <div className="text-[11px] font-semibold text-slate-500 px-1 py-1 border-b border-slate-200 mb-1.5">
                      {section}
                    </div>
                    <div className="space-y-1">
                {sectionItems.map((item) => {
                  const isActive = item.kind === 'single'
                    ? item.flatIndex === currentIndex
                    : item.flatIndices.includes(currentIndex)
                  if (item.kind === 'single') {
                    const q = item.question
                    const val = answerMap[q.question_id]?.value || ((q.user_answer as any)?.value as string | undefined)
                    const imgs = (answerMap[q.question_id]?.images as string[] | undefined) || (((q.user_answer as any)?.images as string[] | undefined) || [])
                    const isAnswered = !!val || imgs.length > 0
                    const isCorrect = q.is_correct === true
                    const isWrong = q.is_correct === false
                    return (
                      <button
                        key={q.question_id}
                        type="button"
                        onClick={() => handleSwitchQuestion(item.flatIndex)}
                        className={[
                          'w-full h-8 rounded text-xs font-medium flex items-center justify-center border transition-colors',
                          isCorrect ? 'border-emerald-500 bg-emerald-500 text-white' : '',
                          isWrong ? 'border-rose-500 bg-rose-500 text-white' : '',
                          !isCorrect && !isWrong && isAnswered ? 'border-indigo-500 bg-indigo-500 text-white' : '',
                          !isCorrect && !isWrong && !isAnswered ? 'border-indigo-300 bg-white text-indigo-600 hover:bg-indigo-50' : '',
                          isActive ? 'ring-2 ring-indigo-400 ring-offset-1' : '',
                        ].filter(Boolean).join(' ')}
                      >
                        {q.index_in_paper}
                      </button>
                    )
                  }
                  // Group item
                  const group = item
                  const answeredCount = group.subQuestions.filter(sq => {
                    const v = answerMap[sq.question_id]?.value
                    const i = answerMap[sq.question_id]?.images
                    return !!v || (i && i.length > 0)
                  }).length
                  const label = GROUP_TYPE_LABEL[group.groupType] || '组'
                  return (
                    <div
                      key={group.groupId}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        // Navigate to first unanswered sub-question, or first if all answered
                        const firstUnanswered = group.subQuestions.findIndex(sq => {
                          const v = answerMap[sq.question_id]?.value
                          const imgs = answerMap[sq.question_id]?.images
                          return !v && !(imgs && imgs.length > 0)
                        })
                        const targetIdx = firstUnanswered >= 0 ? group.flatIndices[firstUnanswered] : group.flatIndices[0]
                        handleSwitchQuestion(targetIdx)
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          const firstUnanswered = group.subQuestions.findIndex(sq => {
                            const v = answerMap[sq.question_id]?.value
                            const imgs = answerMap[sq.question_id]?.images
                            return !v && !(imgs && imgs.length > 0)
                          })
                          const targetIdx = firstUnanswered >= 0 ? group.flatIndices[firstUnanswered] : group.flatIndices[0]
                          handleSwitchQuestion(targetIdx)
                        }
                      }}
                      className={`rounded border text-xs cursor-pointer transition-colors ${isActive ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/50'}`}
                    >
                      <div className="px-2 py-1 font-medium text-slate-700">
                        {label} ({answeredCount}/{group.subQuestions.length})
                      </div>
                      <div className="grid grid-cols-5 gap-1 px-2 pb-1.5">
                        {group.subQuestions.map((sq, subIdx) => {
                          const sqVal = answerMap[sq.question_id]?.value
                          const sqActive = group.flatIndices[subIdx] === currentIndex
                          const sqAnswered = !!sqVal || ((answerMap[sq.question_id]?.images?.length ?? 0) > 0)
                          return (
                            <button
                              key={sq.question_id}
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleSwitchQuestion(group.flatIndices[subIdx]) }}
                              className={[
                                'h-6 w-6 rounded text-[10px] flex items-center justify-center border transition-colors',
                                sqAnswered ? 'border-indigo-500 bg-indigo-500 text-white' : 'border-slate-300 bg-white text-slate-500',
                                sqActive ? 'ring-2 ring-indigo-400' : '',
                              ].filter(Boolean).join(' ')}
                            >
                              {subIdx + 1}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-[11px] text-slate-500">
                共 {questions.length} 题，已作答 {Object.keys(answerMap).length} 题
              </div>
            </div>
            )}
            <div className="flex-1 border rounded-xl p-6 min-h-[260px] shadow-sm bg-slate-50 max-w-4xl mx-auto">
              {currentQuestion ? (
                (() => {
                  // Check if current question belongs to a group
                  const currentItem = groupItems.find(item =>
                    item.kind === 'group'
                      ? item.flatIndices.includes(currentIndex)
                      : item.flatIndex === currentIndex
                  )
                  if (currentItem && currentItem.kind === 'group') {
                    const group = currentItem
                    const subIdx = group.flatIndices.indexOf(currentIndex)
                    return (
                      <CompositeQuestion
                        key={group.groupId}
                        group={group}
                        activeSubIndex={subIdx >= 0 ? subIdx : 0}
                        onSubIndexChange={(newSubIdx) => {
                          handleSwitchQuestion(group.flatIndices[newSubIdx])
                        }}
                        answerMap={answerMap}
                        onAnswerChange={handleTextAnswer}
                        onSelectChoice={handleSelectChoice}
                        onGrade={(qId) => handleGrade(qId)}
                        onExplain={handleExplain}
                        gradingMap={gradingMap}
                        isReadOnly={session?.status === 'submitted'}
                        testMode={testLocked}
                      />
                    )
                  }
                  // Regular single question
                  return (
                    <div className="space-y-3" key={currentQuestion.question_id}>
                      <div className="flex items-center justify-between text-sm text-slate-500">
                        <div>
                          第 {currentQuestion.index_in_paper} 题
                          {currentQuestion.score ? ` · ${currentQuestion.score} 分` : ''}
                        </div>
                        {currentQuestion.difficulty && (
                          <div className="flex items-center gap-0.5" title={`难度：${currentQuestion.difficulty}/5`}>
                            <span className="mr-1">难度：</span>
                            {Array.from({ length: 5 }).map((_, i) => (
                              <Star
                                key={i}
                                className={`w-4 h-4 ${
                                  i < (currentQuestion.difficulty || 0)
                                    ? 'fill-yellow-400 text-yellow-400'
                                    : 'text-slate-300'
                                }`}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                      {renderQuestionCard(currentQuestion)}
                    </div>
                  )
                })()
              ) : (
                <div className="text-sm text-muted-foreground">暂无题目</div>
              )}
            </div>
          </div>
          {summary && (
            <div className="border rounded-md p-4 space-y-3">
              <div className="font-medium">本次成绩摘要</div>
              <div className="text-sm text-muted-foreground space-x-4">
                {typeof summary.total_score === 'number' && <span>总分：{summary.total_score}</span>}
                {typeof summary.correct_count === 'number' && <span>正确：{summary.correct_count}</span>}
                {typeof summary.wrong_count === 'number' && <span>错误：{summary.wrong_count}</span>}
                {typeof summary.unanswered_count === 'number' && <span>未作答：{summary.unanswered_count}</span>}
              </div>

              {/* Knowledge Point Breakdown */}
              {knowledgeSummary && knowledgeSummary.kpBreakdown.length > 0 && (
                <div className="pt-2 border-t">
                  <div className="text-sm font-medium mb-2">知识点掌握情况</div>
                  <div className="space-y-2">
                    {knowledgeSummary.kpBreakdown.map((kp) => {
                      const pct = Math.round(kp.accuracy * 100)
                      const barColor = kp.accuracy >= 0.8 ? 'bg-green-500' : kp.accuracy >= 0.6 ? 'bg-yellow-500' : 'bg-red-500'
                      return (
                        <div key={kp.kpCode} className="flex items-center gap-2">
                          <span className="text-xs text-slate-600 w-24 truncate" title={kp.name}>{kp.name}</span>
                          <div className="flex-1 bg-slate-200 rounded-full h-1.5">
                            <div className={`h-1.5 rounded-full ${barColor}`} style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-xs text-slate-500 w-10 text-right">{pct}%</span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Diagnosis Button */}
              <div className="pt-2 border-t">
                <button
                  type="button"
                  onClick={() => navigate(generatePath(ROUTES.EXAM_DIAGNOSIS, { sessionId: sessionId! }))}
                  className="w-full px-4 py-2 rounded-md bg-blue-50 text-blue-700 text-sm hover:bg-blue-100 transition-colors"
                >
                  📊 查看诊断报告
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Mobile bottom navigation bar */}
      {session && questions.length > 0 && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 shadow-lg">
          <div className="flex items-center justify-between px-4 py-1.5">
            <button
              type="button"
              disabled={currentIndex === 0}
              onClick={() => handleSwitchQuestion(currentIndex - 1)}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg text-slate-600 bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              上一题
            </button>
            <button
              type="button"
              onClick={() => setMobileDrawerOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg text-indigo-600 bg-indigo-50"
            >
              <span>{currentIndex + 1}</span>
              <span className="text-indigo-400">/</span>
              <span>{questions.length}</span>
              <svg className="w-4 h-4 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
            </button>
            <button
              type="button"
              disabled={currentIndex >= questions.length - 1}
              onClick={() => handleSwitchQuestion(currentIndex + 1)}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg text-white bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              下一题
              <ChevronRight className="w-4 h-4 ml-1" />
            </button>
          </div>
          <div className="flex justify-center px-4 pb-2 pt-1">
            <button
              type="button"
              onClick={() => {
                if (currentIndex < questions.length - 1) handleSwitchQuestion(currentIndex + 1)
              }}
              disabled={currentIndex >= questions.length - 1}
              className="inline-flex items-center px-4 py-1.5 text-xs font-medium rounded-full text-orange-600 bg-orange-50 border border-orange-200 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              我不会，跳下一题
            </button>
          </div>
        </div>
      )}

      {/* Mobile question drawer */}
      {mobileDrawerOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileDrawerOpen(false)}
          />
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl max-h-[70vh] flex flex-col animate-slide-up">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <span className="text-sm font-semibold text-slate-700">题目列表</span>
              <button
                type="button"
                onClick={() => setMobileDrawerOpen(false)}
                className="p-1 rounded-md hover:bg-slate-100"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {sections.map(({ section, items: sectionItems }) => (
                <div key={section}>
                  <div className="text-xs font-semibold text-slate-500 px-1 py-1 border-b border-slate-200 mb-2">
                    {section}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {sectionItems.map((item) => {
                      if (item.kind === 'single') {
                        const q = item.question
                        const val = answerMap[q.question_id]?.value || ((q.user_answer as any)?.value as string | undefined)
                        const imgs = (answerMap[q.question_id]?.images as string[] | undefined) || (((q.user_answer as any)?.images as string[] | undefined) || [])
                        const isAnswered = !!val || imgs.length > 0
                        const isCorrect = q.is_correct === true
                        const isWrong = q.is_correct === false
                        const isActive = item.flatIndex === currentIndex
                        return (
                          <button
                            key={q.question_id}
                            type="button"
                            onClick={() => { handleSwitchQuestion(item.flatIndex); setMobileDrawerOpen(false) }}
                            className={[
                              'h-9 w-9 rounded-lg text-xs font-medium flex items-center justify-center border transition-colors',
                              isCorrect ? 'border-emerald-500 bg-emerald-500 text-white' : '',
                              isWrong ? 'border-rose-500 bg-rose-500 text-white' : '',
                              !isCorrect && !isWrong && isAnswered ? 'border-indigo-500 bg-indigo-500 text-white' : '',
                              !isCorrect && !isWrong && !isAnswered ? 'border-indigo-300 bg-white text-indigo-600' : '',
                              isActive ? 'ring-2 ring-indigo-400 ring-offset-1' : '',
                            ].filter(Boolean).join(' ')}
                          >
                            {q.index_in_paper}
                          </button>
                        )
                      }
                      // Group item
                      const group = item
                      const isActive = group.flatIndices.includes(currentIndex)
                      const answeredCount = group.subQuestions.filter(sq => {
                        const v = answerMap[sq.question_id]?.value
                        const i = answerMap[sq.question_id]?.images
                        return !!v || (i && i.length > 0)
                      }).length
                      const label = GROUP_TYPE_LABEL[group.groupType] || '组'
                      return (
                        <div
                          key={group.groupId}
                          className={`rounded-lg border text-xs p-2 ${isActive ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200 bg-white'}`}
                        >
                          <div className="font-medium text-slate-700 mb-1">
                            {label} ({answeredCount}/{group.subQuestions.length})
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {group.subQuestions.map((sq, subIdx) => {
                              const sqVal = answerMap[sq.question_id]?.value
                              const sqActive = group.flatIndices[subIdx] === currentIndex
                              const sqAnswered = !!sqVal || ((answerMap[sq.question_id]?.images?.length ?? 0) > 0)
                              return (
                                <button
                                  key={sq.question_id}
                                  type="button"
                                  onClick={() => { handleSwitchQuestion(group.flatIndices[subIdx]); setMobileDrawerOpen(false) }}
                                  className={[
                                    'h-7 w-7 rounded text-[10px] flex items-center justify-center border transition-colors',
                                    sqAnswered ? 'border-indigo-500 bg-indigo-500 text-white' : 'border-slate-300 bg-white text-slate-500',
                                    sqActive ? 'ring-2 ring-indigo-400' : '',
                                  ].filter(Boolean).join(' ')}
                                >
                                  {subIdx + 1}
                                </button>
                              )
                            })}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}
              <div className="text-xs text-slate-500 pt-2">
                共 {questions.length} 题，已作答 {Object.keys(answerMap).length} 题
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
